/**
 * Lógica pura del buscador de menús (Ctrl+K / ⌘K). Sin React ni Supabase: la
 * UI vive en `components/admin/BuscadorMenus.tsx` y los menús llegan ya
 * filtrados por permisos desde `useDynamicMenus` (la misma fuente del sidebar).
 *
 * Aquí solo se decide: qué opciones salen de esos menús, cómo se ordenan al
 * buscar, qué letras se resaltan, cuáles fueron las recientes y a dónde manda
 * cada una. Todo con tests en `__tests__/buscadorMenus.test.ts`.
 */

// ---------------------------------------------------------------------------
// Opciones a partir de los menús del sidebar
// ---------------------------------------------------------------------------

/** Lo que el buscador necesita de `DynamicMenuChild`. */
export interface SubmenuFuente<I> {
  title: string;
  href: string;
  icon: I;
  submenuId: number;
  disabled?: boolean;
}

/** Lo que el buscador necesita de `DynamicMenuItem`. */
export interface MenuFuente<I> {
  title: string;
  href?: string;
  icon: I;
  menuId: number;
  children?: SubmenuFuente<I>[];
  portalChildren?: SubmenuFuente<I>[];
  isPortal?: boolean;
}

export interface OpcionBuscador<I> {
  /** Estable entre recargas: se guarda en "Recientes". */
  id: string;
  titulo: string;
  /** Menú padre o portal. */
  grupo: string;
  grupoId: number;
  grupoIcono: I;
  icono: I;
  href: string;
  /** Ruta legible: "Portal Cobranza / Conciliaciones". */
  ruta: string;
  /** La entrada directa a un portal (lo mismo que su botón en el sidebar). */
  esEntradaPortal: boolean;
  /** El grupo es un portal (para el chip "Portal" del encabezado). */
  grupoEsPortal: boolean;
  /**
   * Nivel de la fila: `portal` (su entrada), `menu` (menú directo, como
   * Dashboard) o `submenu`. La web no tiene tercer nivel: `submenus_secciones`
   * solo existe para las apps móviles y `useDynamicMenus` no lo trae.
   */
  tipo: "portal" | "menu" | "submenu";
}

/** Texto de la fila que entra a un portal (el nombre del portal ya va en el encabezado). */
export const TITULO_ENTRADA_PORTAL = "Ir al inicio del portal";

/**
 * Aplana los menús del sidebar en opciones buscables, respetando su orden.
 *
 * - Menú con hijos: una opción por submenú.
 * - Menú directo (Dashboard): una opción.
 * - Portal: su entrada (la misma URL que el sidebar) y cada submenú interno al
 *   que el usuario tiene permiso.
 */
export function construirOpciones<I>(menus: readonly MenuFuente<I>[] | null | undefined): OpcionBuscador<I>[] {
  const opciones: OpcionBuscador<I>[] = [];
  const vistos = new Set<string>();
  const texto = (v: unknown) => (typeof v === "string" ? v.trim() : v == null ? "" : String(v).trim());
  const agregar = (o: OpcionBuscador<I>) => {
    // Sin título o sin ruta no hay nada que buscar ni a dónde ir.
    if (!o.titulo || !o.href || vistos.has(o.id)) return;
    vistos.add(o.id);
    opciones.push(o);
  };
  const ruta = (grupo: string, titulo: string) => (grupo && grupo !== titulo ? `${grupo} / ${titulo}` : titulo);

  for (const menu of menus ?? []) {
    if (!menu) continue;
    const grupo = texto(menu.title);
    const href = texto(menu.href);
    const base = { grupo, grupoId: menu.menuId, grupoIcono: menu.icon, grupoEsPortal: !!menu.isPortal };

    if (menu.isPortal && href) {
      agregar({
        ...base,
        id: `menu:${menu.menuId}`,
        titulo: TITULO_ENTRADA_PORTAL,
        icono: menu.icon,
        href,
        // En Recientes y Favoritos la fila va sola: la ruta dice de qué portal es.
        ruta: grupo || "Portal",
        esEntradaPortal: true,
        tipo: "portal",
      });
      for (const sub of menu.portalChildren ?? []) {
        if (!sub || sub.disabled) continue;
        const titulo = texto(sub.title);
        agregar({
          ...base,
          id: `sub:${sub.submenuId}`,
          titulo,
          icono: sub.icon,
          href: texto(sub.href),
          ruta: ruta(grupo, titulo),
          esEntradaPortal: false,
          tipo: "submenu",
        });
      }
      continue;
    }

    if (href) {
      agregar({
        ...base,
        id: `menu:${menu.menuId}`,
        titulo: grupo,
        icono: menu.icon,
        href,
        ruta: grupo,
        esEntradaPortal: false,
        tipo: "menu",
      });
      continue;
    }

    for (const sub of menu.children ?? []) {
      if (!sub || sub.disabled) continue;
      const titulo = texto(sub.title);
      agregar({
        ...base,
        id: `sub:${sub.submenuId}`,
        titulo,
        icono: sub.icon,
        href: texto(sub.href),
        ruta: ruta(grupo, titulo),
        esEntradaPortal: false,
        tipo: "submenu",
      });
    }
  }
  return opciones;
}

// ---------------------------------------------------------------------------
// Normalización
// ---------------------------------------------------------------------------

const MARCAS = /\p{M}/gu;

function normalizarCaracter(ch: string): string {
  if (/\s/.test(ch)) return " ";
  return ch.normalize("NFD").replace(MARCAS, "").toLowerCase();
}

/** Minúsculas, sin acentos ni diéresis, espacios colapsados y recortados. */
export function normalizar(texto: string | null | undefined): string {
  let salida = "";
  for (const ch of String(texto ?? "")) salida += normalizarCaracter(ch);
  return salida.replace(/ +/g, " ").trim();
}

/**
 * Normaliza sin colapsar espacios y guarda, por cada carácter de salida, el
 * índice (en code units) del carácter original: con eso el resaltado cae sobre
 * el texto tal como se pinta, con sus acentos.
 */
export function normalizarConMapa(entrada: string | null | undefined): { texto: string; mapa: number[] } {
  const texto = String(entrada ?? "");
  let salida = "";
  const mapa: number[] = [];
  let i = 0;
  for (const ch of texto) {
    const n = normalizarCaracter(ch);
    for (let k = 0; k < n.length; k++) mapa.push(i);
    salida += n;
    i += ch.length;
  }
  return { texto: salida, mapa };
}

// ---------------------------------------------------------------------------
// Alias simples
// ---------------------------------------------------------------------------

/**
 * Sinónimos de uso diario. La llave es una palabra (normalizada) del nombre del
 * submenú o del menú; los valores son otras formas en que la gente lo busca.
 */
export const ALIAS_MENUS: Readonly<Record<string, readonly string[]>> = {
  cobranza: ["cobros", "pagos"],
  pagos: ["cobranza", "cobros"],
  compradores: ["clientes"],
  clientes: ["compradores"],
  prospectos: ["leads"],
  inmobiliarias: ["agencias"],
  inmobiliaria: ["agencia"],
  agentes: ["asesores", "vendedores"],
  escrituracion: ["escrituras", "notaria"],
  notaria: ["notarios", "escrituras"],
  juridico: ["legal", "abogados"],
  legal: ["juridico"],
  demandas: ["juicios", "juridico"],
  comisiones: ["pago a agentes"],
  conciliaciones: ["conciliar", "stp"],
  reportes: ["informes"],
  dashboard: ["inicio", "tablero"],
  inicio: ["dashboard", "tablero"],
  tablero: ["dashboard"],
  configuracion: ["ajustes"],
  usuarios: ["accesos", "cuentas de usuario"],
  roles: ["permisos"],
  tickets: ["soporte", "postventa"],
  propiedades: ["unidades", "departamentos", "inmuebles"],
  inmuebles: ["propiedades", "unidades"],
  bancos: ["creditos", "hipotecas"],
  embajadores: ["referidos"],
  referidos: ["embajadores"],
  citas: ["agenda", "reservas"],
  reservas: ["citas"],
};

/** Alias de una opción, a partir de las palabras de su submenú y su menú. */
export function aliasDe(titulo: string | null | undefined, grupo: string | null | undefined): string[] {
  const palabras = `${normalizar(titulo)} ${normalizar(grupo)}`.split(/[^a-z0-9]+/);
  const alias = new Set<string>();
  for (const p of palabras) {
    // Solo propiedades propias: "constructor" o "tostring" no deben traer
    // funciones del prototipo de Object.
    if (!Object.prototype.hasOwnProperty.call(ALIAS_MENUS, p)) continue;
    const lista = ALIAS_MENUS[p];
    if (Array.isArray(lista)) for (const a of lista) alias.add(a);
  }
  return [...alias];
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

/** Niveles de coincidencia: al inicio > al inicio de una palabra > subcadena. */
export const NIVEL = { INICIO: 3, PALABRA: 2, SUBCADENA: 1 } as const;

/*
 * Puntaje = base del campo + nivel. Cualquier coincidencia en el nombre del
 * submenú gana a una en el menú padre, y esta a un alias: quien escribe busca
 * casi siempre el submenú. Dentro de cada campo manda el nivel.
 */
const BASE_TITULO = 100;
const BASE_GRUPO = 50;
const BASE_ALIAS = 20;
const puntajeDe = (base: number, nivel: number) => base + nivel * 10;
/** Coincidencia difusa (letras en orden, con huecos): por debajo de todo lo demás. */
const PUNTAJE_DIFUSO = 5;

export interface Coincidencia {
  nivel: number;
  inicio: number;
}

const esAlfanumerico = (ch: string | undefined) => !!ch && /[a-z0-9]/.test(ch);

/** Mejor coincidencia de `consulta` (normalizada) dentro de `texto` (normalizado). */
export function coincidir(texto: string, consulta: string): Coincidencia | null {
  if (!consulta) return null;
  if (texto.startsWith(consulta)) return { nivel: NIVEL.INICIO, inicio: 0 };
  let primera = -1;
  let desde = 0;
  for (;;) {
    const i = texto.indexOf(consulta, desde);
    if (i < 0) break;
    if (primera < 0) primera = i;
    if (!esAlfanumerico(texto[i - 1])) return { nivel: NIVEL.PALABRA, inicio: i };
    desde = i + 1;
  }
  return primera >= 0 ? { nivel: NIVEL.SUBCADENA, inicio: primera } : null;
}

/** Letras de `consulta` en orden dentro de `texto`; devuelve sus índices o null. */
export function coincidirDifuso(texto: string, consulta: string): number[] | null {
  const letras = consulta.replace(/ /g, "");
  if (letras.length < 3) return null;
  const indices: number[] = [];
  let j = 0;
  for (let i = 0; i < texto.length && j < letras.length; i++) {
    if (texto[i] === letras[j]) {
      indices.push(i);
      j++;
    }
  }
  return j === letras.length ? indices : null;
}

/** Rango [inicio, fin) sobre el texto ORIGINAL del título. */
export type Rango = [number, number];

export interface Resultado<I> {
  opcion: OpcionBuscador<I>;
  puntaje: number;
  /** Rangos a resaltar en `opcion.titulo`. */
  rangos: Rango[];
  /**
   * Coincidió solo por el nombre de su menú padre (ni título ni alias). En la
   * vista por grupos esas filas no salen fuera del grupo más exacto.
   */
  soloPorGrupo?: boolean;
}

function rangoOriginal(mapa: number[], original: string, inicio: number, largo: number): Rango {
  const desde = mapa[inicio];
  const ultimo = mapa[inicio + largo - 1];
  // Avanza hasta el final del carácter original (surrogates incluidos).
  const cp = original.codePointAt(ultimo) ?? 0;
  return [desde, ultimo + (cp > 0xffff ? 2 : 1)];
}

/** Puntúa una opción contra la consulta. null = no coincide. */
export function puntuar<I>(opcion: OpcionBuscador<I>, consulta: string | null | undefined): Resultado<I> | null {
  const q = normalizar(consulta);
  if (!q) return { opcion, puntaje: 0, rangos: [] };

  const titulo = normalizarConMapa(opcion.titulo);
  const grupo = normalizar(opcion.grupo);
  const alias = aliasDe(opcion.titulo, opcion.grupo);

  type Evaluacion = { puntaje: number; rango: Rango | null; propio: boolean };
  const evaluar = (termino: string): Evaluacion | null => {
    let mejor: { puntaje: number; rango: Rango | null } | null = null;
    let propio = false; // por título o alias, no solo por el menú padre
    const t = coincidir(titulo.texto, termino);
    if (t) propio = true;
    if (t) {
      mejor = {
        puntaje: puntajeDe(BASE_TITULO, t.nivel),
        rango: rangoOriginal(titulo.mapa, opcion.titulo, t.inicio, termino.length),
      };
    }
    const g = coincidir(grupo, termino);
    if (g && (!mejor || puntajeDe(BASE_GRUPO, g.nivel) > mejor.puntaje)) {
      mejor = { puntaje: puntajeDe(BASE_GRUPO, g.nivel), rango: mejor?.rango ?? null };
    }
    for (const a of alias) {
      const c = coincidir(a, termino);
      if (c) propio = true;
      if (c && (!mejor || puntajeDe(BASE_ALIAS, c.nivel) > mejor.puntaje)) {
        mejor = { puntaje: puntajeDe(BASE_ALIAS, c.nivel), rango: mejor?.rango ?? null };
      }
    }
    return mejor ? { ...mejor, propio } : null;
  };

  // 1) La frase completa.
  const completa = evaluar(q);
  if (completa) {
    return {
      opcion,
      puntaje: completa.puntaje,
      rangos: completa.rango ? [completa.rango] : [],
      soloPorGrupo: !completa.propio,
    };
  }

  // 2) Cada palabra por separado: todas deben aparecer en algún campo.
  const palabras = q.split(" ").filter(Boolean);
  if (palabras.length > 1) {
    const evaluadas = palabras.map(evaluar);
    if (evaluadas.every(Boolean)) {
      const puntajes = evaluadas.map((e) => e!.puntaje);
      const rangos = evaluadas.map((e) => e!.rango).filter((r): r is Rango => !!r);
      return { opcion, puntaje: Math.min(...puntajes) - 1, rangos, soloPorGrupo: evaluadas.every((e) => !e!.propio) };
    }
  }

  // 3) Difusa sobre el título: letras en orden ("cncl" encuentra "Conciliaciones").
  const difusa = coincidirDifuso(titulo.texto, q);
  if (difusa) {
    return {
      opcion,
      puntaje: PUNTAJE_DIFUSO,
      rangos: difusa.map((i) => rangoOriginal(titulo.mapa, opcion.titulo, i, 1)),
    };
  }
  return null;
}

/**
 * Busca y ordena. A igual puntaje gana el título más corto y luego el orden del
 * sidebar. Sin consulta devuelve todo en el orden del sidebar.
 */
export function buscar<I>(opciones: readonly OpcionBuscador<I>[], consulta: string | null | undefined): Resultado<I>[] {
  const resultados: Array<Resultado<I> & { orden: number }> = [];
  opciones.forEach((opcion, orden) => {
    const r = puntuar(opcion, consulta);
    if (r) resultados.push({ ...r, orden });
  });
  if (!normalizar(consulta)) return resultados.map(({ orden: _o, ...r }) => r);
  resultados.sort(
    (a, b) =>
      b.puntaje - a.puntaje ||
      a.opcion.titulo.length - b.opcion.titulo.length ||
      a.orden - b.orden,
  );
  return resultados.map(({ orden: _o, ...r }) => r);
}

export interface GrupoResultados<I> {
  grupoId: number;
  grupo: string;
  icono: I;
  resultados: Resultado<I>[];
}

/** Agrupa por menú/portal conservando el orden de llegada (el mejor grupo primero). */
export function agrupar<I>(resultados: readonly Resultado<I>[]): GrupoResultados<I>[] {
  const grupos = new Map<number, GrupoResultados<I>>();
  for (const r of resultados) {
    let g = grupos.get(r.opcion.grupoId);
    if (!g) {
      g = { grupoId: r.opcion.grupoId, grupo: r.opcion.grupo, icono: r.opcion.grupoIcono, resultados: [] };
      grupos.set(r.opcion.grupoId, g);
    }
    g.resultados.push(r);
  }
  return [...grupos.values()];
}

// ---------------------------------------------------------------------------
// Resaltado
// ---------------------------------------------------------------------------

export interface Segmento {
  texto: string;
  resaltado: boolean;
}

/** Parte `texto` en tramos normales y resaltados; fusiona rangos encimados. */
export function resaltar(entrada: string | null | undefined, rangos: readonly Rango[] | null | undefined): Segmento[] {
  const texto = String(entrada ?? "");
  const limpios = (rangos ?? [])
    .map(([a, b]) => [Math.max(0, a), Math.min(texto.length, b)] as Rango)
    .filter(([a, b]) => b > a)
    .sort((x, y) => x[0] - y[0]);
  const fusionados: Rango[] = [];
  for (const r of limpios) {
    const ultimo = fusionados[fusionados.length - 1];
    if (ultimo && r[0] <= ultimo[1]) ultimo[1] = Math.max(ultimo[1], r[1]);
    else fusionados.push([r[0], r[1]]);
  }
  const segmentos: Segmento[] = [];
  let cursor = 0;
  for (const [a, b] of fusionados) {
    if (a > cursor) segmentos.push({ texto: texto.slice(cursor, a), resaltado: false });
    segmentos.push({ texto: texto.slice(a, b), resaltado: true });
    cursor = b;
  }
  if (cursor < texto.length) segmentos.push({ texto: texto.slice(cursor), resaltado: false });
  return segmentos;
}

// ---------------------------------------------------------------------------
// Recientes (localStorage por usuario)
// ---------------------------------------------------------------------------

export const MAX_RECIENTES = 5;

/** Lo mínimo de `Storage`; inyectable para probar sin navegador. */
export interface AlmacenSimple {
  getItem(clave: string): string | null;
  setItem(clave: string, valor: string): void;
}

export const claveRecientes = (usuario: string) =>
  `sozu:buscador-menus:recientes:${usuario.trim().toLowerCase()}`;

/** Ids recientes (el más nuevo primero). Cualquier fallo de storage = lista vacía. */
export function leerRecientes(almacen: AlmacenSimple | null | undefined, usuario: string | null | undefined): string[] {
  if (!almacen || !usuario) return [];
  try {
    const crudo = almacen.getItem(claveRecientes(usuario));
    if (!crudo) return [];
    const lista: unknown = JSON.parse(crudo);
    if (!Array.isArray(lista)) return [];
    return lista.filter((x): x is string => typeof x === "string").slice(0, MAX_RECIENTES);
  } catch {
    return [];
  }
}

/** Pone `id` al frente, sin duplicados, máximo 5. Devuelve la lista nueva aunque no se pueda guardar. */
export function registrarReciente(
  almacen: AlmacenSimple | null | undefined,
  usuario: string | null | undefined,
  id: string,
): string[] {
  const lista = [id, ...leerRecientes(almacen, usuario).filter((x) => x !== id)].slice(0, MAX_RECIENTES);
  if (almacen && usuario) {
    try {
      almacen.setItem(claveRecientes(usuario), JSON.stringify(lista));
    } catch {
      /* modo privado o storage lleno: los recientes solo viven en memoria */
    }
  }
  return lista;
}

/** Recientes que siguen disponibles para el usuario, en el orden guardado. */
export function opcionesRecientes<I>(
  ids: readonly string[],
  opciones: readonly OpcionBuscador<I>[],
): OpcionBuscador<I>[] {
  const porId = new Map(opciones.map((o) => [o.id, o]));
  return ids.map((id) => porId.get(id)).filter((o): o is OpcionBuscador<I> => !!o);
}

// ---------------------------------------------------------------------------
// Favoritos (localStorage por usuario) y operaciones puras de listas
// ---------------------------------------------------------------------------

/** Favoritos visibles a la vez (no es un límite de cuántos se guardan). */
export const MAX_FAVORITOS_VISIBLES = 10;
/** Lo que sube un favorito al buscar: menos que un nivel de coincidencia (10). */
export const BONO_FAVORITO = 5;

export const claveFavoritos = (usuario: string) =>
  `sozu:buscador-menus:favoritos:${usuario.trim().toLowerCase()}`;

const soloTextos = (lista: unknown): string[] =>
  Array.isArray(lista) ? [...new Set(lista.filter((x): x is string => typeof x === "string" && x.length > 0))] : [];

/** Reciente al frente, sin duplicados, máximo `max`. */
export function agregarReciente(lista: readonly string[], id: string, max = MAX_RECIENTES): string[] {
  return [id, ...lista.filter((x) => x !== id)].slice(0, max);
}

/** Quita `id` de la lista. */
export const quitarId = (lista: readonly string[], id: string): string[] => lista.filter((x) => x !== id);

/** Marca o desmarca un favorito; el recién marcado va primero. */
export function alternarFavorito(lista: readonly string[], id: string): string[] {
  return lista.includes(id) ? quitarId(lista, id) : [id, ...lista];
}

/** Deja solo los ids a los que el usuario todavía tiene acceso. */
export function podar(lista: readonly string[], validos: ReadonlySet<string>): string[] {
  return lista.filter((id) => validos.has(id));
}

function leerLista(almacen: AlmacenSimple | null | undefined, clave: string | null): string[] {
  if (!almacen || !clave) return [];
  try {
    const crudo = almacen.getItem(clave);
    return crudo ? soloTextos(JSON.parse(crudo)) : [];
  } catch {
    return [];
  }
}

function guardarLista(almacen: AlmacenSimple | null | undefined, clave: string | null, lista: readonly string[]) {
  if (!almacen || !clave) return;
  try {
    almacen.setItem(clave, JSON.stringify(lista));
  } catch {
    /* modo privado o storage lleno: la lista solo vive en memoria */
  }
}

const claveDe = (fn: (u: string) => string, usuario: string | null | undefined) => (usuario ? fn(usuario) : null);

export function leerFavoritos(almacen: AlmacenSimple | null | undefined, usuario: string | null | undefined): string[] {
  return leerLista(almacen, claveDe(claveFavoritos, usuario));
}

/** Marca o desmarca y guarda. Devuelve la lista nueva aunque no se pueda guardar. */
export function alternarFavoritoGuardado(
  almacen: AlmacenSimple | null | undefined,
  usuario: string | null | undefined,
  id: string,
): string[] {
  const lista = alternarFavorito(leerFavoritos(almacen, usuario), id);
  guardarLista(almacen, claveDe(claveFavoritos, usuario), lista);
  return lista;
}

export function quitarRecienteGuardado(
  almacen: AlmacenSimple | null | undefined,
  usuario: string | null | undefined,
  id: string,
): string[] {
  const lista = quitarId(leerRecientes(almacen, usuario), id);
  guardarLista(almacen, claveDe(claveRecientes, usuario), lista);
  return lista;
}

export function borrarRecientesGuardados(almacen: AlmacenSimple | null | undefined, usuario: string | null | undefined): string[] {
  guardarLista(almacen, claveDe(claveRecientes, usuario), []);
  return [];
}

/**
 * Quita de favoritos y recientes lo que el usuario ya no puede abrir. `validos`
 * son TODAS sus opciones (no solo las del portal actual): un favorito de otro
 * portal no se pierde por buscar dentro de uno. Solo escribe si algo cambió.
 */
export function podarGuardados(
  almacen: AlmacenSimple | null | undefined,
  usuario: string | null | undefined,
  validos: ReadonlySet<string>,
): { favoritos: string[]; recientes: string[] } {
  const favAntes = leerFavoritos(almacen, usuario);
  const recAntes = leerRecientes(almacen, usuario);
  const favoritos = podar(favAntes, validos);
  const recientes = podar(recAntes, validos);
  if (favoritos.length !== favAntes.length) guardarLista(almacen, claveDe(claveFavoritos, usuario), favoritos);
  if (recientes.length !== recAntes.length) guardarLista(almacen, claveDe(claveRecientes, usuario), recientes);
  return { favoritos, recientes };
}

/** Favoritos visibles en el alcance actual, en el orden guardado, máximo 10. */
export function opcionesFavoritas<I>(ids: readonly string[], opciones: readonly OpcionBuscador<I>[]): OpcionBuscador<I>[] {
  return opcionesRecientes(ids, opciones).slice(0, MAX_FAVORITOS_VISIBLES);
}

/** Sube un poco los favoritos que coinciden con la búsqueda y reordena. */
export function conBonoFavoritos<I>(resultados: readonly Resultado<I>[], favoritos: ReadonlySet<string>): Resultado<I>[] {
  if (favoritos.size === 0) return [...resultados];
  return resultados
    .map((r, orden) => ({ r: favoritos.has(r.opcion.id) ? { ...r, puntaje: r.puntaje + BONO_FAVORITO } : r, orden }))
    .sort(
      (a, b) =>
        b.r.puntaje - a.r.puntaje ||
        a.r.opcion.titulo.length - b.r.opcion.titulo.length ||
        a.orden - b.orden,
    )
    .map((x) => x.r);
}

/** Sección de una fila según el `value` de cmdk. */
export type SeccionFila = "favoritos" | "recientes" | "resultados";
export const PREFIJO_FAVORITO = "favorito:";
export const PREFIJO_RECIENTE = "reciente:";

export function valorDeFila(seccion: SeccionFila, id: string): string {
  if (seccion === "favoritos") return `${PREFIJO_FAVORITO}${id}`;
  if (seccion === "recientes") return `${PREFIJO_RECIENTE}${id}`;
  return id;
}

export function parsearValorFila(valor: string | null | undefined): { seccion: SeccionFila; id: string } | null {
  if (!valor) return null;
  if (valor.startsWith(PREFIJO_FAVORITO)) return { seccion: "favoritos", id: valor.slice(PREFIJO_FAVORITO.length) };
  if (valor.startsWith(PREFIJO_RECIENTE)) return { seccion: "recientes", id: valor.slice(PREFIJO_RECIENTE.length) };
  return { seccion: "resultados", id: valor };
}

/** Atajos dentro del diálogo, con una fila activa. */
export type AccionFila = "alternarFavorito" | "quitarReciente" | null;

/**
 * - Ctrl/⌘+D marca o desmarca favorito (en el diálogo; afuera el navegador lo usa
 *   para marcadores, por eso se intercepta solo aquí).
 * - Supr quita de recientes, solo en el grupo Recientes y sin texto en el input
 *   (con texto, Supr borra hacia adelante como siempre).
 */
export function accionDeTeclaEnFila(
  e: TeclaAtajo,
  esMac: boolean,
  fila: { seccion: SeccionFila } | null,
  hayTexto: boolean,
): AccionFila {
  if (!fila) return null;
  const esD = e.key?.toLowerCase() === "d" || e.code === "KeyD";
  const mod = esMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
  if (esD && mod && !e.altKey && !e.shiftKey) return "alternarFavorito";
  if (e.key === "Delete" && !e.ctrlKey && !e.metaKey && !e.altKey && !e.shiftKey && !hayTexto && fila.seccion === "recientes") {
    return "quitarReciente";
  }
  return null;
}

// ---------------------------------------------------------------------------
// Atajo de teclado
// ---------------------------------------------------------------------------

export interface TeclaAtajo {
  key: string;
  /** Tecla física. Con distribuciones como la española `key` puede no ser "k". */
  code?: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  shiftKey: boolean;
}

/** ¿Es la tecla K? Por `key` o por la tecla física (`KeyK`). */
export const esTeclaK = (e: Pick<TeclaAtajo, "key" | "code">) =>
  e.key?.toLowerCase() === "k" || e.code === "KeyK";

/** Ctrl+K en Windows/Linux, ⌘K en Mac (en Mac, Ctrl+K borra hasta el final de la línea). */
export function esAtajoBuscador(e: TeclaAtajo, esMac: boolean): boolean {
  if (!esTeclaK(e) || e.altKey || e.shiftKey) return false;
  return esMac ? e.metaKey && !e.ctrlKey : e.ctrlKey && !e.metaKey;
}

/** Lo mínimo de un `Element` que hace falta para decidir. */
export interface ObjetivoAtajo {
  tagName?: string;
  type?: string;
  value?: unknown;
  isContentEditable?: boolean;
  closest?: (selector: string) => unknown;
}

/** Contenedor propio del buscador: dentro de él el atajo lo cierra. */
export const ATRIBUTO_BUSCADOR = "data-buscador-menus";
/** Zona que usa Ctrl/⌘+K para otra cosa y no debe perderlo. */
export const ATRIBUTO_ATAJO_PROPIO = "data-atajo-ctrl-k";

/**
 * ¿Hay que dejar pasar el atajo a quien tiene el foco, sin tocarlo?
 *
 * - Editores enriquecidos (contenteditable): Ctrl/⌘+K inserta un enlace.
 * - Cualquier zona marcada con `data-atajo-ctrl-k`.
 *
 * Un input o textarea, con o sin texto, NO lo deja pasar: el navegador no le
 * da ningún uso ahí y, si no se intercepta, Chrome manda el foco a su barra
 * de búsqueda.
 */
export function debeIgnorarAtajo(objetivo: ObjetivoAtajo | null | undefined): boolean {
  if (!objetivo) return false;
  if (objetivo.closest?.(`[${ATRIBUTO_BUSCADOR}]`)) return false;
  if (objetivo.closest?.(`[${ATRIBUTO_ATAJO_PROPIO}]`)) return true;
  return !!objetivo.isContentEditable;
}

/*
 * Qué hace cada combinación (buscador, Agente CEO, Panorama, selector) lo decide
 * `resolverAtajo` en `lib/atajosTeclado.ts`.
 */

// ---------------------------------------------------------------------------
// Destino
// ---------------------------------------------------------------------------

export type Destino =
  | { tipo: "interno"; ruta: string }
  | { tipo: "otroHost"; url: string };

export interface ContextoHost {
  /** Subdominio de portal abierto, o null en el admin / localhost. */
  subdominioActual: string | null;
  /** Prefijo de rutas que monta cada subdominio de portal. */
  prefijos: Record<string, string>;
  /** Origen de cada host (`https://agentes.sozu.com`); "admin" para el árbol completo. */
  host: (clave: string) => string;
}

const bajoPrefijo = (ruta: string, prefijo: string) => ruta === prefijo || ruta.startsWith(`${prefijo}/`);

/**
 * A dónde lleva una opción, siempre en la misma pestaña.
 *
 * - En el admin (o localhost) todas las rutas existen: react-router.
 * - En el subdominio de un portal solo existen las rutas de ese portal; las de
 *   otro portal van a su subdominio y el resto al admin, con recarga completa.
 */
export function resolverDestino(href: string, ctx: ContextoHost): Destino | null {
  if (!href) return null;
  if (/^https?:\/\//i.test(href)) return { tipo: "otroHost", url: href };
  if (!href.startsWith("/")) return null;
  const ruta = href.split(/[?#]/)[0];
  if (!ctx.subdominioActual) return { tipo: "interno", ruta: href };
  const propio = ctx.prefijos[ctx.subdominioActual];
  if (propio && bajoPrefijo(ruta, propio)) return { tipo: "interno", ruta: href };
  const otro = Object.entries(ctx.prefijos).find(([, prefijo]) => bajoPrefijo(ruta, prefijo))?.[0];
  return { tipo: "otroHost", url: `${ctx.host(otro ?? "admin")}${href}` };
}

// ---------------------------------------------------------------------------
// Alcance por portal
// ---------------------------------------------------------------------------

/**
 * Prefijo de rutas de un portal a partir de su enlace: los dos primeros
 * segmentos ("/admin/portal-cobranza/inmuebles" da "/admin/portal-cobranza").
 * Es el mismo criterio con el que el sidebar marca el portal activo.
 */
export const prefijoDePortal = (href: string) => "/" + href.split("/").slice(1, 3).join("/");

/** Rutas de portal en App.tsx (fuera de ellas se está en el admin principal). */
const RUTA_DE_PORTAL = /^\/admin\/(portal-|agent(\/|$)|legal-flow)/;

/**
 * Portal en el que está el usuario, o null en el admin principal.
 *
 * Gana el prefijo más largo entre el enlace del portal y sus submenús, para que
 * un portal cuyas vistas viven bajo otra ruta también se reconozca. En un
 * subdominio de portal se usa además el prefijo que ese host monta.
 * Si la ruta es de portal pero el usuario no tiene ese menú, devuelve null
 * (se busca en todo lo suyo, como antes).
 */
export function portalActual<I>(
  menus: readonly MenuFuente<I>[],
  pathname: string,
  prefijoSubdominio: string | null = null,
): MenuFuente<I> | null {
  const ruta = pathname.split(/[?#]/)[0];
  if (!RUTA_DE_PORTAL.test(ruta) && !prefijoSubdominio) return null;
  let mejor: { menu: MenuFuente<I>; largo: number } | null = null;
  for (const menu of menus) {
    if (!menu.isPortal || !menu.href) continue;
    const prefijos = new Set([menu.href, ...(menu.portalChildren ?? []).map((c) => c.href)].map(prefijoDePortal));
    for (const p of prefijos) {
      const coincide = bajoPrefijo(ruta, p) || (prefijoSubdominio !== null && p === prefijoSubdominio);
      if (coincide && (!mejor || p.length > mejor.largo)) mejor = { menu, largo: p.length };
    }
  }
  return mejor?.menu ?? null;
}

/** Id de la opción fija para volver al admin principal desde un portal. */
export const ID_IR_AL_ADMIN = "admin:inicio";

export interface AlcanceBuscador<I> {
  /** Nombre del portal en el que se busca, o null = todos los menús. */
  portal: string | null;
  opciones: OpcionBuscador<I>[];
}

/**
 * Qué se busca según dónde está el usuario:
 * - En el admin principal: todos sus menús.
 * - Dentro de un portal: solo ese portal y, al final, "Ir al admin principal"
 *   si tiene acceso al admin (Super Admin o algún menú que no sea portal).
 */
export function alcanceBuscador<I>(
  menus: readonly MenuFuente<I>[],
  ctx: { pathname: string; prefijoSubdominio?: string | null; esSuperAdmin: boolean; iconoAdmin: I },
): AlcanceBuscador<I> {
  const portal = portalActual(menus, ctx.pathname, ctx.prefijoSubdominio ?? null);
  if (!portal) return { portal: null, opciones: construirOpciones(menus) };
  const opciones = construirOpciones([portal]);
  const tieneAdmin = ctx.esSuperAdmin || menus.some((m) => !m.isPortal);
  if (tieneAdmin) {
    opciones.push({
      id: ID_IR_AL_ADMIN,
      titulo: "Ir al admin principal",
      grupo: "Admin principal",
      grupoId: -1,
      grupoIcono: ctx.iconoAdmin,
      icono: ctx.iconoAdmin,
      href: "/admin",
      ruta: "Salir del portal al panel de administración",
      esEntradaPortal: false,
      grupoEsPortal: false,
      tipo: "menu",
    });
  }
  return { portal: (portal.title ?? "").trim() || "este portal", opciones };
}

// ---------------------------------------------------------------------------
// Debounce de la búsqueda
// ---------------------------------------------------------------------------

/** Espera tras la última tecla antes de recalcular resultados. */
export const DEBOUNCE_BUSQUEDA_MS = 200;

/**
 * Consulta con la que se calculan los resultados: si el input quedó vacío, vacía
 * al instante (vuelven Favoritos y Recientes sin esperar); si no, la del debounce.
 */
export function consultaParaBuscar(actual: string | null | undefined, conDebounce: string | null | undefined): string {
  return normalizar(actual) ? String(conDebounce ?? "") : "";
}

/** ¿Lo que se ve en pantalla todavía no corresponde a lo escrito? */
export function busquedaPendiente(actual: string | null | undefined, conDebounce: string | null | undefined): boolean {
  return normalizar(actual) !== normalizar(consultaParaBuscar(actual, conDebounce));
}

/** Primera opción que se puede abrir de una búsqueda (para Enter durante la espera). */
export function primeraAbrible<I>(
  resultados: readonly Resultado<I>[],
  bloqueada: (o: OpcionBuscador<I>) => boolean = () => false,
): OpcionBuscador<I> | null {
  return resultados.find((r) => !bloqueada(r.opcion))?.opcion ?? null;
}

// ---------------------------------------------------------------------------
// Selección activa
// ---------------------------------------------------------------------------

/**
 * Qué fila queda resaltada. Solo resaltar: abrir es siempre con Enter o clic.
 *
 * - Cambió la consulta (o se acaba de abrir): la primera, que es la de mayor
 *   puntaje porque `visibles` va en el orden en que se pintan.
 * - Misma consulta: se respeta lo que se eligió con flechas mientras la fila
 *   siga en pantalla (un re-render, marcar un favorito, etc. no la mueve).
 * - Si la fila elegida desapareció: la primera.
 */
export function seleccionSiguiente(
  actual: string,
  visibles: readonly string[],
  consultaCambio: boolean,
  /** La que se prefiere al reiniciar (una fila antes que un encabezado). */
  preferido?: string,
): string {
  if (visibles.length === 0) return "";
  const primero = preferido && visibles.includes(preferido) ? preferido : visibles[0];
  if (consultaCambio || !visibles.includes(actual)) return primero;
  return actual;
}

// ---------------------------------------------------------------------------
// Vista por grupos contraíbles
// ---------------------------------------------------------------------------

export interface GrupoVista<I> {
  grupoId: number;
  grupo: string;
  icono: I;
  esPortal: boolean;
  /** Opciones del grupo en total (el número del chip), no solo las visibles. */
  total: number;
  /** Tiene encabezado que abre y cierra (portales y menús con más de una opción). */
  colapsable: boolean;
  expandido: boolean;
  /** Con consulta: muestra solo las filas que coinciden, no el grupo completo. */
  parcial: boolean;
  /** Filas visibles (vacío si está contraído). */
  resultados: Resultado<I>[];
}

export const claveGruposAbiertos = (usuario: string) =>
  `sozu:buscador-menus:grupos-abiertos:${usuario.trim().toLowerCase()}`;

/** Grupos que el usuario dejó abiertos (ids como texto en localStorage). */
export function leerGruposAbiertos(almacen: AlmacenSimple | null | undefined, usuario: string | null | undefined): number[] {
  return leerLista(almacen, claveDe(claveGruposAbiertos, usuario))
    .map(Number)
    .filter((n) => Number.isFinite(n));
}

/** Abre o cierra un grupo y lo guarda. Devuelve la lista nueva aunque no se pueda guardar. */
export function alternarGrupoGuardado(
  almacen: AlmacenSimple | null | undefined,
  usuario: string | null | undefined,
  grupoId: number,
  abrir?: boolean,
): number[] {
  const actual = leerGruposAbiertos(almacen, usuario);
  const abierto = actual.includes(grupoId);
  const quiere = abrir ?? !abierto;
  const lista = quiere ? (abierto ? actual : [...actual, grupoId]) : actual.filter((g) => g !== grupoId);
  guardarLista(almacen, claveDe(claveGruposAbiertos, usuario), lista.map(String));
  return lista;
}

/** Valor cmdk del encabezado de un grupo. */
export const valorEncabezado = (grupoId: number) => `grupo:${grupoId}`;
export function grupoDeValor(valor: string | null | undefined): number | null {
  if (!valor?.startsWith("grupo:")) return null;
  const n = Number(valor.slice("grupo:".length));
  return Number.isFinite(n) ? n : null;
}

/** Qué tan bien coincide el NOMBRE del grupo con la consulta (misma escala que un título). */
export function puntajeNombreGrupo(grupo: string, consulta: string): number {
  const q = normalizar(consulta);
  if (!q) return 0;
  const c = coincidir(normalizar(grupo), q);
  return c ? puntajeDe(BASE_TITULO, c.nivel) : 0;
}

/**
 * Qué grupos y filas se ven.
 *
 * Sin consulta: todos los grupos en el orden del sidebar. Los portales y menús
 * con más de una opción salen contraídos (solo encabezado con conteo) salvo los
 * que el usuario dejó abiertos; un menú directo (Dashboard) sale como fila.
 *
 * Con consulta:
 * - El grupo cuyo NOMBRE coincide mejor sale expandido con TODAS sus opciones,
 *   en el orden del sidebar (empate: el primero del sidebar).
 * - En los demás solo salen las filas que coinciden por su título o alias, bajo
 *   su encabezado. Las que coinciden solo por el nombre del menú no.
 * - Los grupos sin filas no salen.
 * - `forzados`: grupos que el usuario expandió durante esta consulta.
 * - Orden: por el mejor puntaje del grupo (sus filas, o el de su nombre si es
 *   el más exacto); así la primera fila sigue siendo el mejor resultado.
 */
export function vistaGrupos<I>(
  opciones: readonly OpcionBuscador<I>[],
  consulta: string | null | undefined,
  ctx: { abiertos?: ReadonlySet<number>; favoritos?: ReadonlySet<string>; forzados?: ReadonlySet<number> } = {},
): GrupoVista<I>[] {
  const porGrupo = new Map<number, OpcionBuscador<I>[]>();
  for (const o of opciones) {
    const lista = porGrupo.get(o.grupoId) ?? [];
    lista.push(o);
    porGrupo.set(o.grupoId, lista);
  }
  const base = (id: number, lista: OpcionBuscador<I>[]) => {
    const o = lista[0];
    const colapsable = lista.length > 1 || o.grupoEsPortal;
    return { grupoId: id, grupo: o.grupo, icono: o.grupoIcono, esPortal: o.grupoEsPortal, total: lista.length, colapsable };
  };
  const sinMarcar = (o: OpcionBuscador<I>): Resultado<I> => ({ opcion: o, puntaje: 0, rangos: [] });

  if (!normalizar(consulta)) {
    return [...porGrupo.entries()].map(([id, lista]) => {
      const b = base(id, lista);
      const expandido = !b.colapsable || !!ctx.abiertos?.has(id);
      return { ...b, expandido, parcial: false, resultados: expandido ? lista.map(sinMarcar) : [] };
    });
  }

  const resultados = conBonoFavoritos(buscar(opciones, consulta), ctx.favoritos ?? new Set());
  const porId = new Map(resultados.map((r) => [r.opcion.id, r]));

  let exacto: { id: number; puntaje: number } | null = null;
  for (const [id, lista] of porGrupo) {
    const p = puntajeNombreGrupo(lista[0].grupo, consulta ?? "");
    if (p > 0 && (!exacto || p > exacto.puntaje)) exacto = { id, puntaje: p };
  }

  const vistas: Array<GrupoVista<I> & { puntaje: number; orden: number }> = [];
  let orden = 0;
  for (const [id, lista] of porGrupo) {
    const b = base(id, lista);
    const completo = id === exacto?.id || !!ctx.forzados?.has(id);
    const filas = completo
      ? lista.map((o) => porId.get(o.id) ?? sinMarcar(o))
      : resultados.filter((r) => r.opcion.grupoId === id && !r.soloPorGrupo);
    if (filas.length > 0) {
      const puntaje = Math.max(...filas.map((f) => f.puntaje), id === exacto?.id ? exacto.puntaje : 0);
      vistas.push({ ...b, expandido: true, parcial: !completo, resultados: filas, puntaje, orden });
    }
    orden++;
  }
  vistas.sort(
    (a, b) =>
      b.puntaje - a.puntaje ||
      Number(b.grupoId === exacto?.id) - Number(a.grupoId === exacto?.id) ||
      a.orden - b.orden,
  );
  return vistas.map(({ puntaje: _p, orden: _o, ...v }) => v);
}
